import React,{useState,useEffect} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import {Link,useLocation,useNavigate} from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {useSiteImages} from '../context/SiteImagesContext';
import {useAuth} from '../context/AuthContext';

const FALLBACK_LOGO="https://media-manager-c.questera.ai/greta-media/ab03ff55dc7bcdd9e4a34a93308a2aa656b3e0baf34844f0be8f244dd21175025656600d9573a40a26a1591403a49a1d/images/aW1hZ2UvcG5n/fa81633c8a0059a89852be23eff05663.png";

const Navbar=()=> {
  const [scrolled,setScrolled]=useState(false);
  const [mobileMenuOpen,setMobileMenuOpen]=useState(false);
  const [dropdownOpen,setDropdownOpen]=useState(false);
  const location=useLocation();
  const navigate=useNavigate();
  const {getImage}=useSiteImages();
  const {isLoggedIn,logout}=useAuth();
  const logoUrl=getImage('navbar-logo',FALLBACK_LOGO);

  useEffect(()=> {
    const handleScroll=()=> setScrolled(window.scrollY > 50);
    window.addEventListener('scroll',handleScroll);
    return ()=> window.removeEventListener('scroll',handleScroll);
  },[]);

  const scrollToSection=(targetId)=> {
    const element=document.getElementById(targetId);
    if (element) element.scrollIntoView({behavior:'smooth'});
  };

  const handleNavClick=(event,href)=> {
    if (!href.startsWith('#')) return;

    event.preventDefault();
    const targetId=href.replace('#','');

    if (location.pathname !=='/') {
      navigate('/');
      setTimeout(()=> scrollToSection(targetId),300);
    } else {
      scrollToSection(targetId);
    }

    setMobileMenuOpen(false);
  };

  const handleLogout=()=> {
    logout();
    setMobileMenuOpen(false);
    navigate('/');
  };

  const navLinks=[
    {name:'Home',href:'/'},
    {name:'Features',href:'#features'},
    {name:'Insights',href:'/blog'},
    {name:'Pricing',href:'/pricing'}
  ];

  return (
    <nav className={`fixed left-0 right-0 top-0 z-50 transition-all duration-300 ${scrolled || mobileMenuOpen ? 'bg-white/95 py-2 shadow-sm backdrop-blur-md' : 'bg-transparent py-4'}`} role="navigation" aria-label="Main navigation">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" className="flex items-center cursor-pointer" aria-label="FitPair Home">
          <img src={logoUrl} alt="FitPair Logo - Find a Fitness Buddy Nearby" className="h-20 w-auto object-contain sm:h-24 lg:h-32" width="128" height="128" loading="eager" />
        </Link>

        <div className="hidden items-center gap-6 md:flex lg:gap-8">
          {navLinks.map((link)=> (
            link.href.startsWith('/') ? (
              <Link key={link.name} to={link.href} className="cursor-pointer text-sm font-bold text-gray-600 transition-colors hover:text-brand-red lg:text-base">
                {link.name}
              </Link>
            ) : (
              <a key={link.name} href={link.href} onClick={(event)=> handleNavClick(event,link.href)} className="cursor-pointer text-sm font-bold text-gray-600 transition-colors hover:text-brand-red lg:text-base">
                {link.name}
              </a>
            )
          ))}

          <a href="https://support.fitpair.app" target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-gray-600 transition-colors hover:text-brand-red lg:text-base">
            Support
          </a>

          <div className="relative">
            <button onMouseEnter={()=> setDropdownOpen(true)} onClick={()=> setDropdownOpen(!dropdownOpen)} className="flex items-center gap-1 text-sm font-bold text-gray-600 transition-colors hover:text-brand-red lg:text-base" aria-expanded={dropdownOpen} aria-haspopup="true">
              More+
              <SafeIcon icon={FiIcons.FiChevronDown} className={`transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {dropdownOpen && (
                <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} exit={{opacity:0,y:10}} onMouseLeave={()=> setDropdownOpen(false)} className="absolute right-0 top-full mt-2 w-48 rounded-xl border border-gray-100 bg-white py-2 shadow-xl" role="menu">
                  <Link to="/faq" onClick={()=> setDropdownOpen(false)} className="block px-4 py-2.5 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-50 hover:text-brand-red" role="menuitem">FAQ</Link>
                  <Link to="/privacy-policy" onClick={()=> setDropdownOpen(false)} className="block px-4 py-2.5 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-50 hover:text-brand-red" role="menuitem">Privacy Policy</Link>
                  <Link to="/terms" onClick={()=> setDropdownOpen(false)} className="block px-4 py-2.5 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-50 hover:text-brand-red" role="menuitem">Terms &amp; Conditions</Link>
                  {isLoggedIn && <Link to="/subscription" onClick={()=> setDropdownOpen(false)} className="block px-4 py-2.5 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-50 hover:text-brand-red" role="menuitem">My Subscription</Link>}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {isLoggedIn && (
            <div className="flex items-center gap-3">
              <Link to="/subscription" className="text-sm font-bold text-gray-600 transition-colors hover:text-brand-red">My Plan</Link>
              <button onClick={handleLogout} className="text-sm font-bold text-gray-400 transition-colors hover:text-brand-red">Logout</button>
            </div>
          )}

          <a href="https://fit-pair.com/download" target="_blank" rel="noopener noreferrer" className="rounded-full bg-brand-red px-7 py-3.5 text-sm font-black text-white shadow-glow transition-all hover:bg-brand-darkRed lg:text-base">
            Download App
          </a>
        </div>

        <button className="rounded-lg p-2 text-gray-800 transition-colors hover:bg-gray-100 md:hidden" onClick={()=> setMobileMenuOpen(!mobileMenuOpen)} aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileMenuOpen}>
          <SafeIcon icon={mobileMenuOpen ? FiIcons.FiX : FiIcons.FiMenu} className="text-3xl" />
        </button>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div initial={{opacity:0,height:0}} animate={{opacity:1,height:'auto'}} exit={{opacity:0,height:0}} className="overflow-hidden border-t border-gray-100 bg-white md:hidden">
            <div className="flex flex-col gap-1 p-5">
              {navLinks.map((link)=> (
                link.href.startsWith('/') ? (
                  <Link key={link.name} to={link.href} onClick={()=> setMobileMenuOpen(false)} className="rounded-lg px-2 py-4 text-lg font-black text-gray-800 transition-colors hover:bg-gray-50 hover:text-brand-red">
                    {link.name}
                  </Link>
                ) : (
                  <a key={link.name} href={link.href} onClick={(event)=> handleNavClick(event,link.href)} className="cursor-pointer rounded-lg px-2 py-4 text-lg font-black text-gray-800 transition-colors hover:bg-gray-50 hover:text-brand-red">
                    {link.name}
                  </a>
                )
              ))}

              <a href="https://support.fitpair.app" target="_blank" rel="noopener noreferrer" className="rounded-lg px-2 py-4 text-lg font-black text-gray-800 transition-colors hover:bg-gray-50 hover:text-brand-red">Support</a>
              <Link to="/faq" onClick={()=> setMobileMenuOpen(false)} className="rounded-lg px-2 py-4 text-lg font-black text-gray-800 transition-colors hover:bg-gray-50 hover:text-brand-red">FAQ</Link>
              {isLoggedIn && <Link to="/subscription" onClick={()=> setMobileMenuOpen(false)} className="rounded-lg px-2 py-4 text-lg font-black text-gray-800 transition-colors hover:bg-gray-50 hover:text-brand-red">My Subscription</Link>}
              <Link to="/privacy-policy" onClick={()=> setMobileMenuOpen(false)} className="rounded-lg px-2 py-4 text-lg font-black text-gray-800 transition-colors hover:bg-gray-50 hover:text-brand-red">Privacy Policy</Link>
              <Link to="/terms" onClick={()=> setMobileMenuOpen(false)} className="rounded-lg px-2 py-4 text-lg font-black text-gray-800 transition-colors hover:bg-gray-50 hover:text-brand-red">Terms &amp; Conditions</Link>
              {isLoggedIn && <button onClick={handleLogout} className="rounded-lg px-2 py-4 text-left text-lg font-black text-gray-500 transition-colors hover:bg-gray-50 hover:text-brand-red">Logout</button>}

              <a href="https://fit-pair.com/download" target="_blank" rel="noopener noreferrer" className="mt-4 w-full rounded-xl bg-brand-red py-4 text-center text-lg font-black text-white shadow-lg">
                Download Now
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;